import {
  Component,
  computed,
  inject,
  input,
  signal,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { UserSessionService } from '../../../../core/services/mock-auth.service';
import { hasMeaningfulText, isMissingMeaningfulText } from '../../../../core/validators/text-value.utils';
import { ValidationErrorSection } from '../../validation-error-summary/validation-error-summary.model';
import { getCollaboratorTypeConfig, CollaboratorTypeConfig } from './collaborator-type-config';
import { forkJoin } from 'rxjs';
import { FormDropdownService } from '../../../../core/services/form-dropdown.service';
import { PhoneInputComponent } from '../../../../core/shared/phone-input/phone-input.component';
import { DEFAULT_PHONE_PREFIX, isValidNationalPhoneNumber, splitPhoneNumber } from '../../../../core/shared/phone-input/phone-prefixes';

/** UI row model (superset of every professional type's collaborator fields). */
export interface AdditionalClaimsPhoneRow {
  id: number;
  prefix: string;
  number: string;
}

export interface CollaboratorRow {
  id: number;
  lastName: string;
  firstName: string;
  fiscalCode: string;
  registrationNumber: string;
  professionalOrder: string;
  civilServant: boolean;
  publicBody: string;
  communicationOffice: string;
  mobilePhone: string;
  mobilePhonePrefix: string;
  telephone: string;
  telephonePrefix: string;
  claimsNetworkPhone: string;
  claimsNetworkPhonePrefix: string;
  additionalClaimsNetworkPhones: AdditionalClaimsPhoneRow[];
  contactEmail: string;
  claimsSystemAccess: boolean;
  fiscalCodeError: string;
  lastNameError: boolean;
  firstNameError: boolean;
  registrationNumberError: boolean;
  professionalOrderError: boolean;
  publicBodyError: boolean;
  communicationOfficeError: boolean;
  expanded: boolean;
}

/**
 * Reusable, type-driven "Collaboratori" subsection.
 *
 * Owns its own list of collaborators and loads/saves them through the dedicated
 * bulk endpoints (GET/PUT /professionals/{id}/collaborators). The parent
 * container orchestrates the save (draft vs. filled) via the imperative API:
 * `buildPayload()`, `validateFilled()`, `isEmpty()`, `getFiscalCodes()`.
 */
@Component({
  selector: 'app-collaborators-subsection',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, CheckboxModule, RadioButtonModule, TooltipModule, PhoneInputComponent],
  templateUrl: './collaborators-subsection.component.html',
  styleUrl: './collaborators-subsection.component.scss',
})
export class CollaboratorsSubsectionComponent implements OnInit {
  readonly hasMeaningfulText = hasMeaningfulText;
  readonly isMissingMeaningfulText = isMissingMeaningfulText;

  /** Normalized professional type (AUTO_EXPERT, DOCTOR, LAWYER, ...). */
  professionalType = input<string | null>(null);
  /** Professional ID (falls back to the demo session id when not provided). */
  professionalId = input<string | null>(null);
  /** Force readonly rendering. */
  readonly = input<boolean>(false);
  maxAdditionalClaimsPhones = input<number | null>(null);

  private professionalsService = inject(ProfessionalsService);
  private mockAuthService = inject(UserSessionService);
  private formDropdownService = inject(FormDropdownService);

  collaborators = signal<CollaboratorRow[]>([]);
  contactValidationRequested = signal(false);
  private idCounter = 1;
  private phoneIdCounter = 1;

  config = computed<CollaboratorTypeConfig>(() => getCollaboratorTypeConfig(this.professionalType()));
  isHidden = computed(() => this.config().hidden);
  registrationNumberLabel = computed(() => this.config().registrationNumberLabel ?? 'Numero iscrizione albo nazionale');

  ngOnInit(): void {
    this.loadCollaborators();
  }

  private resolveProfessionalId(): string | null {
    return this.professionalId() || this.mockAuthService.getDemoProfessionalId();
  }

  loadCollaborators(): void {
    const professionalId = this.resolveProfessionalId();
    if (!professionalId) {
      return;
    }

    forkJoin({
      response: this.professionalsService.getCollaborators(professionalId),
      prefixes: this.formDropdownService.getPhonePrefixes(),
    }).subscribe({
      next: ({ response }) => {
        const data = response?.data ?? [];
        if (Array.isArray(data) && data.length > 0) {
          const rows = data.map((c: any) => this.mapFromApi(c));
          this.collaborators.set(rows);
          this.idCounter = rows.length + 1;
        }
      },
      error: (error) => {
        if (error?.status !== 404) {
          console.error('Errore nel caricamento dei collaboratori:', error);
        }
      },
    });
  }

  private mapFromApi(c: any): CollaboratorRow {
    const mobile = this.resolvePhoneParts(c.mobilePhone, c.mobilePhonePrefix);
    const telephone = this.resolvePhoneParts(c.telephone, c.telephonePrefix);
    const claims = this.resolvePhoneParts(c.claimsNetworkPhone, c.claimsNetworkPhonePrefix);
    return {
      id: this.idCounter++,
      lastName: c.lastName || '',
      firstName: c.firstName || '',
      fiscalCode: c.fiscalCode || '',
      registrationNumber: c.professionalRegistrationNumber || '',
      professionalOrder: c.professionalOrder || '',
      civilServant: c.civilServant ?? false,
      publicBody: c.publicBody || '',
      communicationOffice: c.communicationOffice || '',
      mobilePhone: mobile.number,
      mobilePhonePrefix: mobile.prefix,
      telephone: telephone.number,
      telephonePrefix: telephone.prefix,
      claimsNetworkPhone: claims.number,
      claimsNetworkPhonePrefix: claims.prefix,
      additionalClaimsNetworkPhones: (c.additionalClaimsNetworkPhones ?? []).map((phone: { number: string; prefix: string }) => ({
        id: this.phoneIdCounter++,
        ...this.resolvePhoneParts(phone.number, phone.prefix),
      })),
      contactEmail: c.contactEmail || '',
      claimsSystemAccess: c.needsClaimsPortalAccess ?? false,
      fiscalCodeError: '',
      lastNameError: false,
      firstNameError: false,
      registrationNumberError: false,
      professionalOrderError: false,
      publicBodyError: false,
      communicationOfficeError: false,
      expanded: false,
    };
  }

  private resolvePhoneParts(number: string | null | undefined, prefix: string | null | undefined): { prefix: string; number: string } {
    if (prefix?.trim()) {
      return { prefix: prefix.trim(), number: (number ?? '').replace(/\s+/g, '') };
    }
    return splitPhoneNumber(number, this.formDropdownService.getPhonePrefixCodesSync());
  }

  private createEmpty(): CollaboratorRow {
    return {
      id: this.idCounter++,
      lastName: '',
      firstName: '',
      fiscalCode: '',
      registrationNumber: '',
      professionalOrder: '',
      civilServant: false,
      publicBody: '',
      communicationOffice: '',
      mobilePhone: '',
      mobilePhonePrefix: DEFAULT_PHONE_PREFIX,
      telephone: '',
      telephonePrefix: DEFAULT_PHONE_PREFIX,
      claimsNetworkPhone: '',
      claimsNetworkPhonePrefix: DEFAULT_PHONE_PREFIX,
      additionalClaimsNetworkPhones: [],
      contactEmail: '',
      claimsSystemAccess: false,
      fiscalCodeError: '',
      lastNameError: false,
      firstNameError: false,
      registrationNumberError: false,
      professionalOrderError: false,
      publicBodyError: false,
      communicationOfficeError: false,
      expanded: true,
    };
  }

  addCollaborator(): void {
    this.collaborators.update((curr) => [...curr, this.createEmpty()]);
  }

  canAddClaimsPhone(collab: CollaboratorRow): boolean {
    const maximum = this.maxAdditionalClaimsPhones();
    return !this.readonly() && (maximum === null || collab.additionalClaimsNetworkPhones.length < maximum);
  }

  addClaimsPhone(collab: CollaboratorRow): void {
    if (!this.canAddClaimsPhone(collab)) return;
    collab.additionalClaimsNetworkPhones = [...collab.additionalClaimsNetworkPhones, {
      id: this.phoneIdCounter++, prefix: DEFAULT_PHONE_PREFIX, number: '',
    }];
    this.collaborators.set([...this.collaborators()]);
  }

  removeClaimsPhone(collab: CollaboratorRow, phoneId: number): void {
    if (this.readonly()) return;
    collab.additionalClaimsNetworkPhones = collab.additionalClaimsNetworkPhones.filter((phone) => phone.id !== phoneId);
    this.collaborators.set([...this.collaborators()]);
  }

  isDuplicateClaimsPhone(collab: CollaboratorRow, phone: AdditionalClaimsPhoneRow): boolean {
    const number = phone.number.replace(/\s+/g, '');
    if (!number) return false;
    const prefix = phone.prefix || DEFAULT_PHONE_PREFIX;
    if (number === collab.claimsNetworkPhone.replace(/\s+/g, '') && prefix === (collab.claimsNetworkPhonePrefix || DEFAULT_PHONE_PREFIX)) {
      return true;
    }
    return collab.additionalClaimsNetworkPhones.some((other) => other.id !== phone.id
      && other.number.replace(/\s+/g, '') === number && (other.prefix || DEFAULT_PHONE_PREFIX) === prefix);
  }

  removeCollaborator(id: number): void {
    this.collaborators.update((curr) => curr.filter((c) => c.id !== id));
  }

  toggleExpanded(id: number): void {
    this.collaborators.update((curr) =>
      curr.map((c) => (c.id === id ? { ...c, expanded: !c.expanded } : c)),
    );
  }

  collapsedTitle(collab: CollaboratorRow): string {
    const parts = [collab.lastName, collab.firstName]
      .map((p) => (p || '').trim())
      .filter((p) => p.length > 0);
    return parts.join(' · ');
  }

  onAccessChange(collab: CollaboratorRow, value: boolean): void {
    collab.claimsSystemAccess = value;
    this.collaborators.set([...this.collaborators()]);
  }

  onCivilServantChange(collab: CollaboratorRow, value: boolean): void {
    collab.civilServant = value;
    if (!value) {
      collab.publicBody = '';
      collab.communicationOffice = '';
    }
    this.collaborators.set([...this.collaborators()]);
  }

  private isValidFiscalCode(value: string): boolean {
    if (!value) return false;
    return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(value.trim());
  }

  isValidContactEmail(value: string): boolean {
    return !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  /** True when the subsection has no collaborator rows. */
  isEmpty(): boolean {
    return this.collaborators().length === 0;
  }

  validateClaimsPhoneDuplicates(): { valid: boolean; sections: ValidationErrorSection[] } {
    const sections: ValidationErrorSection[] = [];
    for (const [index, collab] of this.collaborators().entries()) {
      const errors = collab.additionalClaimsNetworkPhones
        .filter((phone) => this.isDuplicateClaimsPhone(collab, phone))
        .map((phone) => ({
          field: 'Numero aggiuntivo riservato alla rete liquidativa',
          message: 'Numero gia presente per questo collaboratore.',
          fieldId: `collab-additional-claims-phone-${collab.id}-${phone.id}`,
        }));
      if (errors.length) {
        collab.expanded = true;
        sections.push({ title: `Collaboratore #${index + 1}`, errors });
      }
    }
    if (sections.length) this.collaborators.set([...this.collaborators()]);
    return { valid: sections.length === 0, sections };
  }

  /** Normalized (trim+uppercase) fiscal codes, for cross-list uniqueness (D12). */
  getFiscalCodes(): string[] {
    return this.collaborators()
      .map((c) => (c.fiscalCode || '').trim().toUpperCase())
      .filter((cf) => cf.length > 0);
  }

  /**
   * Validate every row as "riga sempre completa" (FILLED semantics, D15):
   * each row must be fully compiled or removed.
   */
  validateFilled(): { valid: boolean; sections: ValidationErrorSection[] } {
    this.contactValidationRequested.set(true);
    const cfg = this.config();
    const sections: ValidationErrorSection[] = [];
    let valid = true;

    const updated = this.collaborators().map((collab, index) => {
      const errors: ValidationErrorSection['errors'] = [];
      let fiscalCodeError = '';

      const lastNameError = this.isMissingMeaningfulText(collab.lastName);
      if (lastNameError) {
        errors.push({ field: 'Cognome', fieldId: `collab-cognome-${collab.id}` });
      }
      const firstNameError = this.isMissingMeaningfulText(collab.firstName);
      if (firstNameError) {
        errors.push({ field: 'Nome', fieldId: `collab-nome-${collab.id}` });
      }
      if (this.isMissingMeaningfulText(collab.fiscalCode)) {
        fiscalCodeError = 'Il codice fiscale è obbligatorio';
      } else if (!this.isValidFiscalCode(collab.fiscalCode)) {
        fiscalCodeError = 'Formato codice fiscale non valido (16 caratteri)';
      }
      if (fiscalCodeError) {
        errors.push({ field: 'Codice Fiscale', message: fiscalCodeError, fieldId: `collab-cf-${collab.id}` });
      }

      const registrationNumberError = cfg.showRegistrationNumber && this.isMissingMeaningfulText(collab.registrationNumber);
      if (registrationNumberError) {
        errors.push({ field: 'Numero iscrizione albo nazionale', fieldId: `collab-reg-${collab.id}` });
      }
      const professionalOrderError = cfg.showProfessionalOrder && this.isMissingMeaningfulText(collab.professionalOrder);
      if (professionalOrderError) {
        errors.push({ field: 'Ordine professionale', fieldId: `collab-order-${collab.id}` });
      }
      let publicBodyError = false;
      let communicationOfficeError = false;
      if (cfg.showPublicWork && collab.civilServant) {
        publicBodyError = this.isMissingMeaningfulText(collab.publicBody);
        if (publicBodyError) {
          errors.push({ field: 'Ente pubblico', fieldId: `collab-body-${collab.id}` });
        }
        communicationOfficeError = this.isMissingMeaningfulText(collab.communicationOffice);
        if (communicationOfficeError) {
          errors.push({ field: 'Ufficio di comunicazione', fieldId: `collab-office-${collab.id}` });
        }
      }

      const phones = [
        { number: collab.mobilePhone, label: 'Cellulare', fieldId: `collab-mobile-${collab.id}` },
        { number: collab.telephone, label: 'Telefono', fieldId: `collab-telephone-${collab.id}` },
        { number: collab.claimsNetworkPhone, label: 'Numero di cellulare/telefono riservato alla rete liquidativa', fieldId: `collab-claims-phone-${collab.id}` },
      ];
      for (const phone of phones) {
        if (phone.number && !isValidNationalPhoneNumber(phone.number)) {
          errors.push({ field: phone.label, fieldId: phone.fieldId });
        }
      }
      for (const phone of collab.additionalClaimsNetworkPhones) {
        const fieldId = `collab-additional-claims-phone-${collab.id}-${phone.id}`;
        if (phone.number.trim() && !isValidNationalPhoneNumber(phone.number)) {
          errors.push({ field: 'Numero aggiuntivo riservato alla rete liquidativa', fieldId });
        }
        if (this.isDuplicateClaimsPhone(collab, phone)) {
          errors.push({ field: 'Numero aggiuntivo riservato alla rete liquidativa', message: 'Numero gia presente per questo collaboratore.', fieldId });
        }
      }
      if (!this.isValidContactEmail(collab.contactEmail)) {
        errors.push({ field: 'Email di contatto', message: 'Formato email non valido', fieldId: `collab-contact-email-${collab.id}` });
      }

      if (errors.length > 0) {
        valid = false;
        sections.push({ title: `Collaboratore #${index + 1}`, errors });
      }

      return {
        ...collab,
        expanded: errors.length > 0 || collab.expanded,
        fiscalCodeError,
        lastNameError,
        firstNameError,
        registrationNumberError,
        professionalOrderError,
        publicBodyError,
        communicationOfficeError,
      };
    });

    this.collaborators.set(updated);
    return { valid, sections };
  }

  /** Build the BE payload (bulk PUT /collaborators). Q9: full object, null for N/A fields. */
  buildPayload(): any[] {
    const cfg = this.config();
    return this.collaborators().map((c) => {
      return {
        firstName: c.firstName || null,
        lastName: c.lastName || null,
        fiscalCode: c.fiscalCode || null,
        professionalRegistrationNumber: cfg.showRegistrationNumber ? c.registrationNumber || null : null,
        professionalOrder: cfg.showProfessionalOrder ? c.professionalOrder || null : null,
        civilServant: cfg.showPublicWork ? c.civilServant : null,
        publicBody: cfg.showPublicWork && c.civilServant ? c.publicBody || null : null,
        communicationOffice: cfg.showPublicWork && c.civilServant ? c.communicationOffice || null : null,
        needsClaimsPortalAccess: c.claimsSystemAccess,
        mobilePhone: c.mobilePhone || null,
        mobilePhonePrefix: c.mobilePhone ? c.mobilePhonePrefix || DEFAULT_PHONE_PREFIX : null,
        telephone: c.telephone || null,
        telephonePrefix: c.telephone ? c.telephonePrefix || DEFAULT_PHONE_PREFIX : null,
        claimsNetworkPhone: c.claimsNetworkPhone || null,
        claimsNetworkPhonePrefix: c.claimsNetworkPhone ? c.claimsNetworkPhonePrefix || DEFAULT_PHONE_PREFIX : null,
        additionalClaimsNetworkPhones: c.additionalClaimsNetworkPhones
          .filter((phone) => phone.number.trim())
          .map((phone) => ({ number: phone.number.replace(/\s+/g, ''), prefix: phone.prefix || DEFAULT_PHONE_PREFIX })),
        contactEmail: c.contactEmail.trim() || null,
        // Pivot v2 (Codici SOL / MFA): la mail di accesso non viene più raccolta in
        // anagrafica, ma nella fase MFA per associazione SOL ↔ persona.
        axaSystemAccessEmail: null,
        personalEmail: null,
      };
    });
  }
}
